import React, { useRef, useState } from "react";
import { PhotoIcon } from "@heroicons/react/20/solid";
import { toast } from "react-toastify";

import { uploadFileToServer } from "../../utils/ZulalWebAPIV2"; // update this to call v2 endpoint

const LoyaltyCard = () => {
  const inputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const maxFileSize = 2 * 1024 * 1024; // 2MB
  const allowedTypes = ["image/jpeg", "image/png"];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) return;

    if (!allowedTypes.includes(selectedFile.type)) {
      setError("Only JPG and PNG files are allowed");
      setFile(null);
      return;
    }

    if (selectedFile.size > maxFileSize) {
      setError("File size exceeds 2MB");
      setFile(null);
      return;
    }

    setError("");
    setFile(selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isUploading) return;

    if (!file) {
      setError("Please select a JPG or PNG file first");
      return;
    }

    try {
      setIsUploading(true);
      setError("");

      // V2: send ONLY the file (identity comes from secure cookie session)
      const formData = new FormData();
      formData.append("file", file);

      // optional: backend can ignore this; it must not use it for storage name
      formData.append("filename", file.name);

      const response = await uploadFileToServer(formData);

      // Adjust this according to your real API response shape:
      // recommended backend response: { success: true, message, data }
      if (response?.success) {
        toast.success(response?.message || "File uploaded successfully");
        setFile(null);

        // reset file input UI
        if (inputRef.current) inputRef.current.value = "";
        return;
      }

      toast.error(response?.message || "Upload failed");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Upload failed"
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
          Discount Card
          <p className="text-base text-darkTextColor font-poppins font-normal">
            <br /> One image can be uploaded at a time. Please allow 24 hours to
            validate the card and apply price list to your account.
          </p>
        </div>
      </div>

      <div className="flex items-start justify-self-start font-poppins">
        <div className="col-span-full">
          <form onSubmit={handleSubmit}>
            <label
              htmlFor="file-upload"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Zulal Partners Card Upload
            </label>

            <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
              <div className="text-center">
                <PhotoIcon
                  aria-hidden="true"
                  className="mx-auto h-12 w-12 text-gray-300"
                />

                <div className="mt-4 flex text-sm leading-6 text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <p className="font-poppins text-primary hover:text-primaryHover">
                      Upload a file
                    </p>
                    <input
                      ref={inputRef}
                      id="file-upload"
                      name="file"
                      type="file"
                      accept="image/png,image/jpeg"
                      className="sr-only"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                  </label>

                  <p className="pl-1">or drag and drop</p>
                </div>

                <p className="text-xs leading-5 text-gray-600">
                  PNG, JPG up to 2MB
                </p>

                {file?.name && (
                  <p className="mt-2 text-xs text-gray-700">
                    Selected: <span className="font-medium">{file.name}</span>
                  </p>
                )}
              </div>
            </div>

            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}

            <div className="mt-6">
              <button
                type="submit"
                disabled={isUploading || !file}
                className="w-full px-4 py-2 text-sm font-medium leading-5 text-textWhite bg-secondary hover:bg-secondaryHover
                           border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                           disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <span className="inline-flex items-center gap-2">
                  {isUploading && (
                    <span
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                      aria-label="Loading"
                    />
                  )}
                  {isUploading ? "Uploading..." : "Submit"}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyCard;
